import React, { useEffect, useRef, useState } from 'react';

interface TestimonialsProps {
  language: string;
}

const testimonialImages = [
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nrYWaxaBiLh8CG3Y1ze06PgAcj7pmqwsyWuxN",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n0xS66WAvFCfhkUXT2AWzsS9OqR5mrHEbn8YI",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8ngZ2cFfmEPIx8Z3nzKl6JQGbvO9Td4s0R5Hph",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nTvv1zr4v017gl9M3SEQb2IXxmcAWOaUT5fBh",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nzox2qwgNtI6pPX8SQuv0Kr4cjEOflWMdRH5y",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nGGAyDN1YeJQ43fzVZSbEoHTOhylk6MGDCx20",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nvkVUzrPz59IYgTPVE47yhWlBZAnufsxod0aN",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nLZPxcrNYF0986rOZbDvnoEjmuyacANPdz5HQ",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8ndxlMUdGSi8AtxC91XonHZIakbrg0cNMlBEuY",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nYksI6gMS9TeH3O5cyYVjnoC27ZmD4lQazpkR",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8ngx3CuXEPIx8Z3nzKl6JQGbvO9Td4s0R5HphC",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8nGvFKyG1YeJQ43fzVZSbEoHTOhylk6MGDCx20",
  "https://l0ry9zjg4l.ufs.sh/f/70f42tTcLu8n6Kt1U1iBQ5XKN27otwPyu3DAmSIj6ET1gY9L"
];

const Testimonials: React.FC<TestimonialsProps> = ({ language }) => {
  const [isHovered, setIsHovered] = useState(false);
  const row1Ref = useRef<HTMLDivElement>(null);
  const row2Ref = useRef<HTMLDivElement>(null);

  const content = {
    en: {
      title: "Our Happy Clients",
      subtitle: "Join our community of satisfied clients who have experienced the transformative power of our treatments."
    },
    da: {
      title: "Vores Glade Klienter",
      subtitle: "Bliv en del af vores fællesskab af tilfredse klienter, som har oplevet den transformerende effekt af vores behandlinger."
    }
  };

  return (
    <section className="py-20 bg-[#1A1A1A] overflow-hidden" id="testimonials">
      <div className="container mx-auto px-4 mb-16">
        <div className="text-center">
          <h2 
            className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {content[language as keyof typeof content].title}
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            {content[language as keyof typeof content].subtitle}
          </p>
        </div>
      </div>

      <div 
        className="relative w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Gradient Overlays */}
        <div className="absolute left-0 top-0 w-32 h-full bg-gradient-to-r from-[#1A1A1A] to-transparent z-10" />
        <div className="absolute right-0 top-0 w-32 h-full bg-gradient-to-l from-[#1A1A1A] to-transparent z-10" />

        {/* First Row - Left to Right */}
        <div className="mb-8 py-4 overflow-hidden">
          <div 
            ref={row1Ref}
            className={`flex gap-8 animate-scroll-left ${isHovered ? 'pause-animation' : ''}`}
            style={{ width: 'fit-content' }}
          >
            {[...testimonialImages, ...testimonialImages].map((src, index) => (
              <div
                key={`row1-${index}`}
                className="relative group"
              >
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]">
                  <img
                    src={src}
                    alt={`Client ${index + 1}`}
                    className="w-full h-full object-cover lazy-load"
                    loading="lazy"
                    onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-all duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Second Row - Right to Left */}
        <div className="py-4 overflow-hidden">
          <div 
            ref={row2Ref}
            className={`flex gap-8 animate-scroll-right ${isHovered ? 'pause-animation' : ''}`}
            style={{ width: 'fit-content' }}
          >
            {[...testimonialImages.reverse(), ...testimonialImages].map((src, index) => (
              <div
                key={`row2-${index}`}
                className="relative group"
              >
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]">
                  <img
                    src={src}
                    alt={`Client ${index + 1}`}
                    className="w-full h-full object-cover lazy-load"
                    loading="lazy"
                    onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-all duration-300" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ambient Background Effect */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent)] pointer-events-none" />
      </div>

      <style jsx>{`
        .pause-animation {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default Testimonials;